import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const CLINIC_NAME = 'The Healing Clinic';

/**
 * Export tabular data to a real .xlsx workbook (SheetJS).
 *
 * @param {string} filename  base file name (without extension)
 * @param {Array<{name?: string, columns: {key:string,label:string}[], rows: object[]}>} sheets
 *        one entry per worksheet. `rows` are plain objects keyed by column.key.
 */
export function exportToXlsx(filename, sheets) {
  const wb = XLSX.utils.book_new();
  const list = Array.isArray(sheets) ? sheets : [sheets];

  list.forEach((sheet, i) => {
    const columns = sheet.columns || [];
    const headerLabels = columns.map((c) => c.label);
    const aoa = [
      headerLabels,
      ...(sheet.rows || []).map((r) => columns.map((c) => {
        const v = r[c.key];
        return v == null ? '' : v;
      })),
    ];
    // Totaling & Summary ledger lines (dynamic — computed by the caller over the filtered set).
    if (sheet.summary?.length) {
      aoa.push([]);
      aoa.push(['TOTALING & SUMMARY']);
      sheet.summary.forEach((s) => aoa.push([s.label, s.value]));
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Auto-fit-ish column widths from header + sampled cell lengths.
    ws['!cols'] = columns.map((c) => {
      const maxLen = Math.max(
        String(c.label).length,
        ...(sheet.rows || []).slice(0, 200).map((r) => String(r[c.key] ?? '').length),
      );
      return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
    });
    const name = (sheet.name || `Sheet${i + 1}`).slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });

  XLSX.writeFile(wb, `${filename}.xlsx`);
}

/**
 * Export a formatted, paginated PDF table (jsPDF + autoTable).
 * Includes a repeating document header (clinic + title + range + generated timestamp),
 * grid padding, and automatic page-break logic. Does NOT use window.print.
 *
 * @param {string} filename  base file name (without extension)
 * @param {object} opts
 * @param {string} opts.title     report title
 * @param {string} [opts.subtitle] e.g. the active date range / filters
 * @param {{key:string,label:string}[]} opts.columns
 * @param {object[]} opts.rows
 * @param {'portrait'|'landscape'} [opts.orientation]
 */
export function exportToPdf(filename, { title, subtitle, columns, rows, orientation = 'portrait', summary }) {
  const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

  const head = [columns.map((c) => c.label)];
  const body = (rows || []).map((r) => columns.map((c) => {
    const v = r[c.key];
    return v == null ? '' : String(v);
  }));

  autoTable(doc, {
    head,
    body,
    startY: 86,
    margin: { top: 86, left: 40, right: 40, bottom: 40 },
    styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak', valign: 'middle' },
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    // Repeating document header + footer on every page.
    didDrawPage: () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(17, 24, 39);
      doc.text(CLINIC_NAME, 40, 36);

      doc.setFontSize(11);
      doc.setTextColor(55, 65, 81);
      doc.text(title || 'Report', 40, 54);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      if (subtitle) doc.text(subtitle, 40, 68);
      doc.text(`Generated: ${generatedAt}`, pageWidth - 40, 36, { align: 'right' });

      const page = doc.internal.getNumberOfPages();
      doc.text(`Page ${page}`, pageWidth - 40, pageHeight - 20, { align: 'right' });

      doc.setDrawColor(209, 213, 219);
      doc.line(40, 76, pageWidth - 40, 76);
    },
  });

  // Totaling & Summary ledger (dynamic — recalculated by the caller from the filtered data set).
  if (summary?.length) {
    let y = doc.lastAutoTable.finalY + 16;
    const need = 22 + summary.length * 13;
    if (y + need > pageHeight - 40) { doc.addPage(); y = 96; }
    doc.setDrawColor(209, 213, 219); doc.line(40, y, pageWidth - 40, y); y += 14;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(17, 24, 39);
    doc.text('TOTALING & SUMMARY', 40, y); y += 13;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(55, 65, 81);
    summary.forEach((s) => { doc.text(`${s.label}: ${s.value}`, 48, y); y += 12; });
  }

  doc.save(`${filename}.pdf`);
}

// ────────────────────────────────────────────────────────────────────────────────
// Multi-section report exports
//
// A report tab shows several things at once — a KPI row, two charts, three tables. The older
// exportToPdf/exportToXlsx emit exactly ONE table, so every tab used to export a fraction of
// what was on screen. These builders take a list of titled sections instead, so an export can
// reproduce the whole tab, and each section carries its own Total / Average line.
//
// Section shapes:
//   { kind: 'kpi', title, items: [{ label, value }] }
//   { title, columns: [{ key, label, numeric?, money?, total?, avg? }], rows, totals?: 'auto' | [{label,value}] | false }
//
// Numeric columns should carry RAW NUMBERS in `rows`; the exporter formats them, which is what
// lets it total them correctly. Columns not declared numeric are sniffed — a column whose every
// non-blank cell parses as a number is treated as numeric.
//
// TOTAL vs AVERAGE — deliberately asymmetric:
//   • TOTAL   is the default for numeric columns. Opt out with `total: false` (rates, shares,
//     and any column where a sum is nonsense).
//   • AVERAGE is OPT-IN via `avg: true`, and the AVERAGE line is omitted entirely when no column
//     asks for one. Averaging every numeric column produces meaningless figures — the mean of a
//     "share of total" or of a lead count per status says nothing — and the clutter buries the
//     numbers that do matter. Only mark `avg: true` where the mean is genuinely informative
//     (a per-session rate, an average receipt value).
// ────────────────────────────────────────────────────────────────────────────────

const moneyPdf = (v) => `INR ${Math.round(v || 0).toLocaleString('en-IN')}`;

/** Parse a cell to a number, tolerating "INR 1,200", "1,200", "45%" and blanks. */
function parseNum(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v == null || v === '' || v === '—') return null;
  const s = String(v);
  if (!/\d/.test(s)) return null;
  const n = Number(s.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Decide which columns can be totalled. An explicit `numeric: true` always wins; otherwise a
 * column qualifies only if every non-blank cell in it parses as a number, so an ID or a code
 * column that happens to be digits-only in one row is not silently summed.
 */
function numericColumns(columns, rows) {
  return columns.filter((c) => {
    if (c.numeric === false) return false;
    if (c.numeric === true) return true;
    let seen = 0;
    for (const r of rows) {
      const raw = r[c.key];
      if (raw == null || raw === '' || raw === '—') continue;
      if (parseNum(raw) == null) return false;
      seen += 1;
    }
    return seen > 0;
  });
}

/**
 * Build the footer lines for a section.
 *
 * TOTAL covers every numeric column that has not opted out (`total: false`).
 * AVERAGE covers only columns that opted in (`avg: true`), and is omitted entirely when none did.
 */
function totalsRows(columns, rows) {
  const numeric = new Set(numericColumns(columns, rows).map((c) => c.key));
  if (numeric.size === 0) return [];
  const moneyKeys = new Set(columns.filter((c) => c.money).map((c) => c.key));

  const totalKeys = new Set(columns.filter((c) => numeric.has(c.key) && c.total !== false).map((c) => c.key));
  const avgKeys = new Set(columns.filter((c) => numeric.has(c.key) && c.avg === true).map((c) => c.key));
  if (totalKeys.size === 0 && avgKeys.size === 0) return [];

  const sums = {};
  const counts = {};
  for (const r of rows) {
    for (const key of numeric) {
      const n = parseNum(r[key]);
      if (n == null) continue;
      sums[key] = (sums[key] ?? 0) + n;
      counts[key] = (counts[key] ?? 0) + 1;
    }
  }

  const fmt = (key, value) => (moneyKeys.has(key)
    ? moneyPdf(value)
    : (Math.round(value * 100) / 100).toLocaleString('en-IN'));

  const line = (label, keys, pick) => columns.map((c, i) => {
    if (i === 0) return label;
    if (!keys.has(c.key)) return '';
    const n = pick(c.key);
    return n == null ? '' : fmt(c.key, n);
  });

  const out = [];
  if (totalKeys.size > 0) out.push(line('TOTAL', totalKeys, (k) => sums[k] ?? 0));
  if (avgKeys.size > 0) out.push(line('AVERAGE', avgKeys, (k) => (counts[k] ? sums[k] / counts[k] : null)));
  return out;
}

/** Format a section's rows into autoTable/aoa body cells, applying money formatting. */
function bodyCells(columns, rows) {
  const moneyKeys = new Set(columns.filter((c) => c.money).map((c) => c.key));
  return rows.map((r) => columns.map((c) => {
    const v = r[c.key];
    if (v == null || v === '') return '';
    if (moneyKeys.has(c.key)) {
      const n = parseNum(v);
      return n == null ? String(v) : moneyPdf(n);
    }
    return String(v);
  }));
}

/**
 * Full-report PDF: every section on screen, each with its own title and Total/Average line.
 *
 * @param {string} filename
 * @param {object} opts
 * @param {string}   opts.title       report title (repeated in the page header)
 * @param {string}   [opts.subtitle]  active date range / filters
 * @param {object[]} opts.sections    see the section shapes documented above
 * @param {'portrait'|'landscape'} [opts.orientation]
 */
export function exportReportPdf(filename, { title, subtitle, sections = [], orientation = 'portrait' }) {
  const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

  const drawHeader = () => {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(17, 24, 39);
    doc.text(CLINIC_NAME, 40, 34);
    doc.setFontSize(11); doc.setTextColor(55, 65, 81);
    doc.text(title || 'Report', 40, 52);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(107, 114, 128);
    if (subtitle) doc.text(subtitle, 40, 66);
    doc.text(`Generated: ${generatedAt}`, pageW - 40, 34, { align: 'right' });
    doc.setDrawColor(209, 213, 219); doc.line(40, 74, pageW - 40, 74);
  };

  drawHeader();
  let y = 92;
  const ensure = (need) => { if (y + need > pageH - 50) { doc.addPage(); drawHeader(); y = 92; } };

  const sectionTitle = (text) => {
    ensure(34);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(17, 24, 39);
    doc.text(text, 40, y);
    y += 10;
  };

  sections.filter(Boolean).forEach((s) => {
    // KPI sections render as a plain two-column Metric/Value table so the headline numbers on
    // screen always appear in the file — they used to be dropped from every export.
    if (s.kind === 'kpi') {
      const items = (s.items || []).filter(Boolean);
      if (items.length === 0) return;
      if (s.title) sectionTitle(s.title);
      autoTable(doc, {
        startY: y + 6,
        margin: { top: 86, left: 40, right: 40 },
        head: [['Metric', 'Value']],
        body: items.map((k) => [String(k.label), String(k.value ?? '')]),
        styles: { fontSize: 8.5, cellPadding: 5, overflow: 'linebreak' },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: () => drawHeader(),
      });
      y = doc.lastAutoTable.finalY + 20;
      return;
    }

    const rows = s.rows || [];
    const columns = s.columns || [];
    if (rows.length === 0 || columns.length === 0) return;

    if (s.title) sectionTitle(s.title);

    const foot = s.totals === false ? [] : (Array.isArray(s.totals) ? [] : totalsRows(columns, rows));

    autoTable(doc, {
      startY: y + 6,
      margin: { top: 86, left: 40, right: 40 },
      head: [columns.map((c) => c.label)],
      body: bodyCells(columns, rows),
      foot,
      showFoot: foot.length > 0 ? 'lastPage' : 'never',
      styles: { fontSize: 8, cellPadding: 4.5, overflow: 'linebreak', valign: 'middle' },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [229, 234, 242], textColor: [17, 24, 39], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      didDrawPage: () => drawHeader(),
    });
    y = doc.lastAutoTable.finalY + 14;

    // Caller-supplied summary lines (used where a total isn't a simple column sum — e.g. a rate).
    if (Array.isArray(s.totals) && s.totals.length > 0) {
      ensure(20 + s.totals.length * 12);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(55, 65, 81);
      s.totals.forEach((line) => { doc.text(`${line.label}: ${line.value}`, 48, y); y += 12; });
      y += 8;
    }
    y += 6;
  });

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(107, 114, 128);
    doc.text(`Page ${i} of ${pages}`, pageW - 40, pageH - 20, { align: 'right' });
  }

  doc.save(`${filename}.pdf`);
}

/** Excel twin of exportReportPdf — one worksheet per section, same Total/Average lines. */
export function exportReportXlsx(filename, sections = []) {
  const wb = XLSX.utils.book_new();
  const used = new Set();
  // Worksheet names are capped at 31 chars and must be unique, so a truncated collision gets
  // a numeric suffix rather than throwing.
  const safeName = (raw, i) => {
    let base = (raw || `Sheet${i + 1}`).replace(/[\\/*?:[\]]/g, ' ').slice(0, 31).trim() || `Sheet${i + 1}`;
    let name = base;
    let n = 2;
    while (used.has(name.toLowerCase())) {
      const suffix = ` (${n})`;
      name = base.slice(0, 31 - suffix.length) + suffix;
      n += 1;
    }
    used.add(name.toLowerCase());
    return name;
  };

  sections.filter(Boolean).forEach((s, i) => {
    let aoa;
    let columns = [];

    if (s.kind === 'kpi') {
      const items = (s.items || []).filter(Boolean);
      if (items.length === 0) return;
      columns = [{ key: 'metric', label: 'Metric' }, { key: 'value', label: 'Value' }];
      aoa = [['Metric', 'Value'], ...items.map((k) => [k.label, k.value ?? ''])];
    } else {
      const rows = s.rows || [];
      columns = s.columns || [];
      if (rows.length === 0 || columns.length === 0) return;
      aoa = [columns.map((c) => c.label), ...bodyCells(columns, rows)];
      if (s.totals !== false && !Array.isArray(s.totals)) {
        const foot = totalsRows(columns, rows);
        if (foot.length > 0) { aoa.push([]); foot.forEach((r) => aoa.push(r)); }
      }
      if (Array.isArray(s.totals) && s.totals.length > 0) {
        aoa.push([]);
        aoa.push(['TOTALING & SUMMARY']);
        s.totals.forEach((line) => aoa.push([line.label, line.value]));
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = columns.map((c, ci) => {
      const maxLen = Math.max(
        String(c.label).length,
        ...aoa.slice(1, 201).map((r) => String(r[ci] ?? '').length),
      );
      return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
    });
    XLSX.utils.book_append_sheet(wb, ws, safeName(s.title, i));
  });

  if (wb.SheetNames.length === 0) throw new Error('Nothing to export');
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

/**
 * Three-section Daily Ops PDF — each section starts on its own page so they never bleed
 * into one another. Page 1: Appointments, Page 2: Staff Performance, Page 3: Call Logs.
 *
 * @param {string} filename  base file name (without extension)
 * @param {object} opts
 * @param {string}   opts.rangeLabel      formatted date range shown in every header
 * @param {object[]} opts.invoices        invoice rows for the patient billing ledger
 * @param {object}   opts.billingSummary  { totalBilled, totalPaid, outstanding, invoiceCount }
 * @param {object[]} opts.staff           staff performance rows from staffPerformanceQuery
 * @param {object[]} opts.callLogs        call log rows from listCallLogs
 * @param {object}   opts.callSummary     analytics object from callLogAnalyticsQuery — supplies the
 *                                        mutually exclusive call buckets so this PDF agrees with
 *                                        the Call Logs and Leads tabs
 * @param {object[]} opts.paymentModes    per-method payment aggregation from the operational report
 * @param {object[]} opts.procedures      procedure revenue rows (quantity > 0 only)
 */
export function exportDailyOpsPdf(filename, {
  rangeLabel = '',
  invoices = [],
  billingSummary = {},
  staff = [],
  callLogs = [],
  callSummary = null,
  paymentModes = [],
  procedures = [],
}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  const money = (v) => `INR ${Math.round(v || 0).toLocaleString('en-IN')}`;
  const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' }) : '—';

  // Reusable branded section header — called at section start and repeated on overflow pages.
  const drawHeader = (sectionTitle) => {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(17, 24, 39);
    doc.text(CLINIC_NAME, 40, 34);
    doc.setFontSize(11); doc.setTextColor(55, 65, 81);
    doc.text(sectionTitle, 40, 52);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(107, 114, 128);
    if (rangeLabel) doc.text(rangeLabel, 40, 66);
    doc.text(`Generated: ${generatedAt}`, pageW - 40, 34, { align: 'right' });
    doc.setDrawColor(209, 213, 219); doc.line(40, 74, pageW - 40, 74);
  };

  // Horizontal "KPI band" — renders a row of small metric tiles at position y, returns new y.
  const drawKpiBand = (items, y) => {
    const colW = (pageW - 80) / items.length;
    items.forEach(([label, val], i) => {
      const x = 40 + i * colW;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(17, 24, 39);
      doc.text(String(val), x, y);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(107, 114, 128);
      doc.text(label, x, y + 13);
    });
    return y + 30;
  };

  // ── PAGE 1 : BILLING DETAILS ───────────────────────────────────────────────
  drawHeader('Billing Details');

  const billingKpis = [
    ['Invoiced Amount', money(billingSummary.totalBilled)],
    ['Collected', money(billingSummary.totalPaid)],
    ['Outstanding', money(billingSummary.outstanding)],
    ['Invoices', billingSummary.invoiceCount ?? invoices.length],
  ];
  let y = drawKpiBand(billingKpis, 92);

  autoTable(doc, {
    startY: y,
    margin: { top: 86, left: 40, right: 40 },
    head: [['Patient Name', 'Patient ID', 'Amount', 'Paid', 'Procedure / Treatment', 'Outstanding', 'Payment Mode']],
    body: invoices.map((i) => [
      i.patientName ?? '—',
      i.patientCode ?? '—',
      money(i.totalAmount),
      money(i.amountPaid),
      i.treatmentSummary ?? '—',
      money(i.balanceDue),
      i.paymentMethods || '—',
    ]),
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    didDrawPage: () => drawHeader('Billing Details'),
  });

  // ── Transactions by payment source (Cash / Card / UPI / …) ────────────────
  if (paymentModes.length > 0) {
    let py = doc.lastAutoTable.finalY + 22;
    if (py + 40 > pageH - 50) { doc.addPage(); drawHeader('Billing Details'); py = 92; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(17, 24, 39);
    doc.text('Transactions by Payment Source', 40, py);
    autoTable(doc, {
      startY: py + 8,
      margin: { top: 86, left: 40, right: 40 },
      head: [['Payment Source', 'Transactions', 'Amount']],
      body: paymentModes.map((p) => [p.method, String(p.count ?? 0), money(p.amount)]),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      didDrawPage: () => drawHeader('Billing Details'),
    });
  }

  // ── Invoiced amount & sessions booked by Procedure / Treatment (booked > 0 only) ──────────
  if (procedures.length > 0) {
    let qy = doc.lastAutoTable.finalY + 22;
    if (qy + 40 > pageH - 50) { doc.addPage(); drawHeader('Billing Details'); qy = 92; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(17, 24, 39);
    doc.text('Invoiced Amount & Sessions Booked by Procedure / Treatment', 40, qy);
    autoTable(doc, {
      startY: qy + 8,
      margin: { top: 86, left: 40, right: 40 },
      head: [['Procedure / Treatment', 'Sessions Booked', 'Invoiced Amount']],
      body: procedures.map((p) => [p.name, String(p.quantity ?? 0), money(p.revenue)]),
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      didDrawPage: () => drawHeader('Billing Details'),
    });
  }

  // ── PAGE 2 : STAFF PERFORMANCE ─────────────────────────────────────────────
  // Sessions = completed appointments; Patients = distinct patients among THOSE completed
  // appointments (never a broader "ever touched" count — see ReportService.GetStaffPerformanceListAsync
  // for why Patients must stay in the same universe as Sessions); Invoiced Amount is the actual
  // billed total for the range (same figure as the Billing Details page), not a per-staff sum —
  // pro-rata attribution is still shown per-row in the table below.
  doc.addPage();
  drawHeader('Staff Performance');

  const staffKpis = [
    ['Staff Members', staff.length],
    ['Total Sessions', staff.reduce((sum, x) => sum + (x.sessionsConducted ?? 0), 0)],
    ['Invoiced Amount', money(billingSummary.totalBilled)],
  ];
  y = drawKpiBand(staffKpis, 92);

  autoTable(doc, {
    startY: y,
    margin: { top: 86, left: 40, right: 40 },
    head: [['Staff Name', 'Sessions', 'Patients Treated', 'Revenue (INR)']],
    body: staff.map((s) => [
      s.fullName ?? '—',
      String(s.sessionsConducted ?? 0),
      String(s.patientsHandled ?? 0),
      money(s.revenueAttributed),
    ]),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [5, 150, 105], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    didDrawPage: () => drawHeader('Staff Performance'),
  });

  // ── PAGE 3 : CALL LOG ANALYTICS ────────────────────────────────────────────
  doc.addPage();
  drawHeader('Call Log Analytics');

  // Mutually-exclusive buckets, taken from the analytics endpoint when available so this page
  // matches the Call Logs and Leads tabs exactly. Falls back to partitioning the rows locally
  // with the SAME priority (Converted → Existing Patient → Existing Lead → Other) if analytics
  // did not load, so the four still sum to Total either way.
  const totalCalls = callSummary?.totalLogs ?? callLogs.length;
  const converted = callSummary?.convertedToLead
    ?? callLogs.filter((c) => c.status === 'Converted').length;
  const existingPatient = callSummary?.existingPatientOnly
    ?? callLogs.filter((c) => c.status !== 'Converted' && c.isExistingPatient).length;
  const existingLead = callSummary?.existingLeadOnly
    ?? callLogs.filter((c) => c.status !== 'Converted' && !c.isExistingPatient && c.isExistingLead).length;
  const other = callSummary?.otherCalls
    ?? (totalCalls - converted - existingPatient - existingLead);

  const callKpis = [
    ['Total Calls', totalCalls],
    ['Converted to Lead', converted],
    ['Existing Patient', existingPatient],
    ['Existing Lead', existingLead],
    ['Others', other],
  ];
  y = drawKpiBand(callKpis, 92);

  autoTable(doc, {
    startY: y,
    margin: { top: 86, left: 40, right: 40 },
    head: [['Timestamp', 'Caller Name / Number', 'Call Attempts', 'Status', 'Disposition / Action']],
    body: callLogs.map((c) => [
      fmtTs(c.lastCallAt ?? c.receivedAt ?? c.createdAt),
      c.callerName
        ? `${c.callerName} (${c.phoneNumberRaw ?? c.phoneNumber ?? ''})`.trim()
        : (c.phoneNumberRaw ?? c.phoneNumber ?? '—'),
      String(c.totalCalls ?? 1),
      c.isDiscarded ? 'Discarded' : (c.status ?? '—'),
      c.routedTo ?? (c.status === 'Converted' ? 'Converted to Lead/Patient' : '—'),
    ]),
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    didDrawPage: () => drawHeader('Call Log Analytics'),
  });

  // Stamp page numbers on every page now that all autoTable runs are done.
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(107, 114, 128);
    doc.text(`Page ${i} of ${totalPages}`, pageW - 40, pageH - 20, { align: 'right' });
  }

  doc.save(`${filename}.pdf`);
}

/**
 * Practitioner-grouped executive Calendar/Appointment PDF.
 * Layout: [Header] → [Doctor block loop: name + summary row + breakdown table + per-doctor totals]
 *         → [Footer: consolidated clinic totals + timestamp + page numbers].
 *
 * @param {string} filename
 * @param {object} data  CalendarExportDto { doctors[], total* }
 * @param {object} opts  { title, rangeLabel, pageBreakPerDoctor }
 * @param {boolean} [opts.pageBreakPerDoctor=true]  When true (spec #4 "doctor isolation"), every
 *        doctor's block starts on a fresh page with the branding header repeated, so no practitioner's
 *        table is split awkwardly across sheets. autoTable still paginates a single very long doctor
 *        table cleanly (column header repeats; no orphan rows).
 */
export function exportCalendarPdf(filename, data, { title = 'Appointment Report', rangeLabel = '', pageBreakPerDoctor = true } = {}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  const money = (v) => `INR ${Math.round(v || 0).toLocaleString('en-IN')}`;

  const header = () => {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(17, 24, 39);
    doc.text(CLINIC_NAME, 40, 34);
    doc.setFontSize(11); doc.setTextColor(55, 65, 81); doc.text(title, 40, 52);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(107, 114, 128);
    if (rangeLabel) doc.text(rangeLabel, 40, 66);
    doc.text(`Generated: ${generatedAt}`, pageW - 40, 34, { align: 'right' });
    doc.setDrawColor(209, 213, 219); doc.line(40, 74, pageW - 40, 74);
  };

  header();
  let y = 92;
  const ensure = (need) => { if (y + need > pageH - 50) { doc.addPage(); header(); y = 92; } };

  (data?.doctors || []).forEach((d, idx) => {
    // Doctor isolation (spec #4): each practitioner starts fresh on a new page with the branding
    // header repeated. The first doctor uses the page already opened by the initial header().
    if (pageBreakPerDoctor && idx > 0) { doc.addPage(); header(); y = 92; }
    ensure(64);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(37, 99, 235);
    doc.text(`Dr. ${d.doctorName}`, 40, y); y += 14;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(55, 65, 81);
    doc.text(`New Patients Visited: ${d.newPatientsVisited}   |   Total Sessions Conducted: ${d.sessionsConducted}   |   Total Payments Collected: ${money(d.paymentsCollected)}`, 40, y);

    // Per the print-engine spec: Duration (Hrs) and Status columns are omitted; Session #
    // shows the package drawdown fraction (e.g. 5/7); Payment shows the dues lookup on
    // the first session ("Dues Remaining: X") or the day's collection.
    autoTable(doc, {
      startY: y + 8,
      margin: { top: 86, left: 40, right: 40 },
      head: [['Date', 'Time', 'Patient Name', 'Session #', 'Payment']],
      body: (d.appointments || []).map((a) => [
        a.date, a.time, a.patientName,
        a.sessionFraction ?? a.sessionNumber ?? '—',
        a.paymentDisplay ?? (a.payment ? money(a.payment) : '—'),
      ]),
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      didDrawPage: () => header(),
    });
    y = doc.lastAutoTable.finalY + 8;

    ensure(26);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(55, 65, 81);
    doc.text(`Total Checked Out: ${d.checkedOut}   |   Total Cancelled: ${d.cancelled}   |   Total No-Shows: ${d.noShow}`, 40, y);
    y += 22;
  });

  // Consolidated clinic totals (footer band on the final content position).
  ensure(40);
  doc.setDrawColor(209, 213, 219); doc.line(40, y, pageW - 40, y); y += 14;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(17, 24, 39);
  doc.text(
    `Clinic Totals — Checked Out: ${data?.totalCheckedOut ?? 0} | Cancelled: ${data?.totalCancelled ?? 0} | No-Shows: ${data?.totalNoShow ?? 0} | Sessions: ${data?.totalSessions ?? 0} | New Patients: ${data?.totalNewPatients ?? 0} | Payments: ${money(data?.totalPayments)}`,
    40, y,
  );

  // Page numbers on every page.
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(107, 114, 128);
    doc.text(`Page ${i} of ${pages}`, pageW - 40, pageH - 20, { align: 'right' });
  }

  doc.save(`${filename}.pdf`);
}
